import * as React from "react";
import { useVariableValue } from "@devcycle/react-client-sdk";
import { Card } from "antd5";

import { RouterTextLink } from "components/actions/RouterLink";
import { formatNumber } from "components/app_layout/Typography";
import ContactCard from "components/form_components/ContactCard";
import { generateSupplierUrl } from "components/suppliers/utils";
import Divider from "components/ui/divider/Divider";
import { EllipsisTooltipTextLink } from "lib/core_components/EllipsisTooltip";
import { NewRelevanceScore } from "lib/core_components/NewRelevanceScore";
import { PropertyInline } from "lib/core_components/Property";
import { TextHighlighter } from "lib/core_components/TextHighlighter";
import { RELEVANCY_SEARCH_NEW_USERS } from "lib/featureFlags";
import { FrameworkDetails } from "lib/hooks/api/frameworks/useDescribeFramework";
import { FrameworkHighlightResponse } from "lib/hooks/api/frameworks/useFrameworkHighlight";
import { EventDataTypes, EventNames, logEvent } from "lib/tracking";
import { ProcedureType } from "lib/types/graphQLEnums";
import { isDefined, pluralise } from "lib/utils";
import { SignalEntityType } from "lib/utils/signalUtils";
import FrameworkTimeline from "./FrameworkTimeline";

import css from "./FrameworkOverview.module.scss";

type Props = {
  framework: FrameworkDetails | undefined;
  highlights: FrameworkHighlightResponse | undefined;
  isLoading: boolean;
};

export default function FrameworkOverview({
  framework,
  highlights,
  isLoading,
}: Props): JSX.Element {
  const isRelevancySearchEnabled = useVariableValue(RELEVANCY_SEARCH_NEW_USERS, false);

  return (
    <div className={css.frameworkDetailsContent}>
      <div className={css.detailsContainer} aria-label="Framework overview">
        {framework &&
          framework.textSections?.map((section) => (
            <div className={css.section} key={`${section.title} ${section.role}`}>
              <h2 className={css.subtitle}>
                <TextHighlighter
                  content={section.title}
                  wordsToHighlight={highlights?.description}
                />
              </h2>
              <pre className={css.description}>
                <TextHighlighter
                  content={section.content.content}
                  wordsToHighlight={highlights?.description}
                />
              </pre>
            </div>
          ))}
        <Divider className={css.divider} />
        <div className={css.section}>
          {framework && (
            <FrameworkTimeline
              timeline={framework.timeline}
              extensionAvailable={framework.extensionAvailable}
              stage={framework.stage}
              isLoading={isLoading}
            />
          )}
        </div>
        <Divider className={css.divider} />
        {framework && (
          <div className={css.section}>
            <h2 className={css.subtitle}>Lot information</h2>
            <FrameworkLotDetails framework={framework} highlights={highlights} />
          </div>
        )}
      </div>
      <div className={css.summaryDetailsContainer}>
        <Card loading={isLoading} aria-label="Framework key info">
          <h3 className={css.smallerTitle}>Details</h3>
          <div className={css.summaryDetails}>
            {/* TODO: Change to external website */}
            {framework && (
              <>
                {!isRelevancySearchEnabled && (
                  <PropertyInline
                    title="Signal score"
                    value={
                      <NewRelevanceScore
                        entityType={SignalEntityType.Framework}
                        id={framework.id}
                        relevanceScore={framework.signalScore}
                        eventAttributes={{ "Data type": EventDataTypes.frameworks }}
                        popoverAlignment="center"
                      />
                    }
                    className={css.detailLabel}
                    borderBottom
                  />
                )}
                <PropertyInline
                  title="Provider"
                  value={
                    framework.serviceProvider?.id ? (
                      <RouterTextLink
                        to="/buyers/$id"
                        params={{ id: String(framework.serviceProvider.id) }}
                        onClick={() =>
                          logEvent(EventNames.navToBuyerProfile, {
                            "Data type": EventDataTypes.frameworks,
                            "Context source": "Framework provider",
                          })
                        }
                      >
                        {framework.serviceProvider.name}
                      </RouterTextLink>
                    ) : (
                      <>{framework.serviceProvider?.name ?? "-"}</>
                    )
                  }
                  className={css.detailLabel}
                  borderBottom
                />
                <PropertyInline
                  title="Contracting auth."
                  value={
                    framework.contractingAuthority?.id ? (
                      <RouterTextLink
                        to="/buyers/$id"
                        params={{ id: String(framework.contractingAuthority.id) }}
                        onClick={() =>
                          logEvent(EventNames.navToBuyerProfile, {
                            "Data type": EventDataTypes.frameworks,
                            "Context source": "Contracting authority",
                          })
                        }
                      >
                        {framework.contractingAuthority.name}
                      </RouterTextLink>
                    ) : (
                      <>{framework.contractingAuthority?.name ?? "-"}</>
                    )
                  }
                  className={css.detailLabel}
                  borderBottom
                />
                <PropertyInline
                  title="Value"
                  value={formatNumber({
                    value: framework.value?.amount,
                    locale: "en-GB",
                    currency: framework.value?.currency,
                  })}
                  className={css.detailLabel}
                  borderBottom
                />
                {isDefined(framework.procedureType) && (
                  <PropertyInline
                    title="Type"
                    value={getProcedureType(framework.procedureType)}
                    className={css.detailLabel}
                    borderBottom
                  />
                )}
                <PropertyInline
                  title="Lots"
                  value={framework.lots.length}
                  className={css.detailLabel}
                  borderBottom
                />
                <PropertyInline
                  title="Suppliers"
                  value={framework.suppliers?.length ?? "0"}
                  className={css.detailLabel}
                  borderBottom
                />

                {framework.sourceUrl && (
                  <PropertyInline
                    title="Source"
                    value={
                      <EllipsisTooltipTextLink
                        linkText={framework.sourceUrl}
                        linkProps={{
                          to: framework.sourceUrl,
                          targetType: "new-tab",
                          showIcon: true,
                          onClick: () =>
                            logEvent(EventNames.navToSourceDocs, {
                              "Data type": EventDataTypes.frameworks,
                            }),
                        }}
                        fullText={framework.sourceUrl}
                        containerClassname={css.sourceLink}
                      />
                    }
                    className={css.detailLabel}
                  />
                )}
              </>
            )}
          </div>
        </Card>

        <ContactCard
          email={framework?.contactDetails?.email ?? ""}
          phone={framework?.contactDetails?.phone ?? ""}
          address={framework?.contactDetails?.address ?? ""}
          name={framework?.contactDetails?.name ?? ""}
          isLoading={isLoading}
          className={css.contactLabel}
        />
      </div>
    </div>
  );
}

type LotDetailsProps = {
  framework: FrameworkDetails;
  highlights: FrameworkHighlightResponse | undefined;
};
function FrameworkLotDetails({ framework, highlights }: LotDetailsProps): JSX.Element {
  const supplierCount = pluralise(framework.suppliers?.length ?? 0, "supplier");
  return (
    <div aria-label="Framework lot info">
      <span>
        There are a total of{" "}
        {framework.suppliers?.length ? (
          <RouterTextLink
            to="/frameworks/$framework_id/suppliers"
            params={{ framework_id: String(framework.id) }}
            onClick={() =>
              logEvent(EventNames.suppliersClicked, {
                "Context source": "Lot information all suppliers",
              })
            }
          >
            {supplierCount}
          </RouterTextLink>
        ) : (
          <>{supplierCount}</>
        )}{" "}
        across <b>{pluralise(framework.lots.length, "lot")}</b> on this Framework
      </span>
      <div className={css.lotsContainer}>
        {framework.lots.map((lot) => (
          <div key={lot.id} className={css.lotContent}>
            <h3 className={css.lotTitle}>
              <TextHighlighter
                content={`${lot.id}: ${lot?.title ?? ""}`}
                wordsToHighlight={highlights?.lotTitle}
              />
            </h3>
            {lot.description?.content && (
              <pre className={css.description}>
                <TextHighlighter
                  content={lot.description?.content}
                  wordsToHighlight={highlights?.lotDescription}
                />
              </pre>
            )}
            <div className={css.lotProperties}>
              {!!lot.supplierGuids?.length && (
                <PropertyInline
                  title="Suppliers:"
                  value={
                    <RouterTextLink
                      to={generateSupplierUrl(
                        { lotIds: [lot.id] },
                        `/frameworks/${framework.id}/suppliers`,
                      )}
                      onClick={() =>
                        logEvent(EventNames.suppliersClicked, {
                          "Context source": "Lot suppliers",
                        })
                      }
                    >
                      {pluralise(lot.supplierGuids.length, "supplier")}
                    </RouterTextLink>
                  }
                />
              )}
              {framework.value?.amount && (
                <PropertyInline
                  title="Lot value"
                  value={formatNumber({
                    value: framework.value.amount,
                    locale: "en-GB",
                    currency: framework.value?.currency,
                  })}
                />
              )}
              {lot.contractPeriod?.endDate && (
                <PropertyInline title="Expiry date" value={lot.contractPeriod?.endDate} />
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function getProcedureType(procedureType: ProcedureType) {
  switch (procedureType) {
    case "FRAMEWORK":
      return "Framework";
    case "DYNAMIC_PURCHASING_SYSTEM":
      return "DPS";
  }
}
